'use client';

import React from 'react';

/**
 * TextArea mode types for different visual styles
 */
export type TextAreaMode = 'primary' | 'secondary' | 'danger';

/**
 * TextArea component properties
 */
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  mode?: TextAreaMode;
  fullWidth?: boolean;
  glow?: boolean;
}

/**
 * Neon-themed textarea component with glow effects
 * @param {TextAreaProps} props - TextArea properties
 * @returns {JSX.Element} Styled textarea with neon border and glow
 */
export const TextArea: React.FC<TextAreaProps> = ({
  className = '',
  mode = 'primary',
  fullWidth = false,
  glow = false,
  ...props
}) => {
  const baseClasses = 'bg-black border-2 rounded-md transition-all duration-300 focus:outline-none focus:ring-2';

  const modeClasses = {
    primary: 'focus:border-cyan-400 border-cyan-400 text-cyan-400 focus:ring-cyan-400 placeholder-cyan-400/50 input-focus-glow-primary',
    secondary: 'focus:border-fuchsia-400 border-fuchsia-400 text-fuchsia-400 focus:ring-fuchsia-400 placeholder-fuchsia-400/50 input-focus-glow-secondary',
    danger: 'focus:border-danger border-danger text-danger focus:ring-danger placeholder-danger/50 input-focus-glow-danger',
  } as const;

  const widthClasses = fullWidth ? 'w-full' : '';

  const classes = `${baseClasses} ${modeClasses[mode]} ${widthClasses} ${className}`.trim();

  return <textarea x-component-name="TextArea" className={classes} {...props} />;
};